import { COUNTRY_CODES, type CountryCode } from '@noconflict/contracts';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, type ComboboxOption } from '@/shared/ui/combobox';

/** Every ISO country named in `locale` (`Intl.DisplayNames`), sorted by that name. */
export function countryOptions(locale: string): (ComboboxOption & { value: CountryCode })[] {
  const names = new Intl.DisplayNames([locale], { type: 'region' });
  return COUNTRY_CODES.map((value) => ({ value, label: names.of(value) ?? value })).sort((a, b) =>
    a.label.localeCompare(b.label, locale),
  );
}

type CountrySelectProps = Omit<
  React.ComponentProps<typeof Combobox>,
  'options' | 'value' | 'onValueChange' | 'searchLabel' | 'emptyText'
> & {
  value?: CountryCode | undefined;
  onValueChange?: (value: CountryCode) => void;
};

/** Searchable country picker: ISO 3166-1 alpha-2 value, names in the viewer's language. */
function CountrySelect({ value, onValueChange, placeholder, ...props }: CountrySelectProps) {
  const { t, i18n } = useTranslation('common');
  const options = React.useMemo(() => countryOptions(i18n.language), [i18n.language]);
  return (
    <Combobox
      data-slot="country-select"
      options={options}
      value={value}
      onValueChange={(next) => onValueChange?.(next as CountryCode)}
      placeholder={placeholder ?? t('countrySelect.placeholder')}
      searchLabel={t('countrySelect.search')}
      emptyText={t('countrySelect.empty')}
      {...props}
    />
  );
}

export { CountrySelect };
